import { localized, msg } from '@lit/localize';
import { css, html, LitElement } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import '@spectrum-web-components/field-label/sp-field-label.js';
import '@spectrum-web-components/menu/sp-menu-item.js';
import '@spectrum-web-components/picker/sp-picker.js';
import '@spectrum-web-components/table/sp-table.js';
import '@spectrum-web-components/table/sp-table-body.js';
import '@spectrum-web-components/table/sp-table-cell.js';
import '@spectrum-web-components/table/sp-table-head.js';
import '@spectrum-web-components/table/sp-table-head-cell.js';
import '@spectrum-web-components/table/sp-table-row.js';

import { api } from '../../api';
import type { UsageSummary } from '../../api/types';
import { getLocale } from '../../i18n/localization';
import { formatCurrencyCents, formatDuration } from '../../utils/format';

@customElement('kv-module-usage')
@localized()
export class KvModuleUsage extends LitElement {
  static styles = css`
    :host {
      display: block;
      padding: 24px;
      min-height: 100%;
      overflow: auto;
      box-sizing: border-box;
      color: var(--color-app-text, #1f1f1f);
    }

    h1 {
      margin: 0 0 16px;
      font-size: 20px;
      font-weight: 600;
    }

    h3 {
      margin: 0 0 10px;
      font-size: 15px;
      font-weight: 600;
    }

    .selector-row {
      display: flex;
      flex-wrap: wrap;
      gap: 16px;
      align-items: end;
      margin-bottom: 16px;
      padding: 12px;
      border: 1px solid var(--color-border, #ddd);
      border-radius: 8px;
      background: var(--color-card-bg, #fff);
    }

    .selector-control {
      min-width: 160px;
    }

    .section {
      margin-bottom: 24px;
    }

    .error {
      color: #c62828;
      margin-top: 8px;
    }

    .muted {
      color: var(--color-app-text-secondary, #6b7280);
    }

    .row {
      cursor: default;
    }
  `;

  @state() accessor selectedYear = new Date().getUTCFullYear();
  @state() accessor selectedMonth = new Date().getUTCMonth() + 1;
  @state() accessor loading = false;
  @state() accessor errorMessage: string | null = null;
  @state() accessor summary: UsageSummary | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    void this.loadSummary();
  }

  private _requestId = 0;

  private async loadSummary() {
    const requestId = ++this._requestId;
    this.loading = true;
    this.errorMessage = null;
    try {
      const result = await api.usage.getSummary({
        year: this.selectedYear,
        month: this.selectedMonth,
      });
      if (requestId !== this._requestId) return;
      this.summary = result;
    } catch (error) {
      if (requestId !== this._requestId) return;
      this.errorMessage =
        error instanceof Error
          ? error.message
          : msg('Failed to load usage data');
      this.summary = null;
    } finally {
      if (requestId === this._requestId) this.loading = false;
    }
  }

  private onYearChange = (event: Event) => {
    const value = Number((event.target as HTMLInputElement).value);
    if (!Number.isInteger(value)) return;
    this.selectedYear = value;
    this.clampMonthToCurrentIfNeeded();
    void this.loadSummary();
  };

  private onMonthChange = (event: Event) => {
    const value = Number((event.target as HTMLInputElement).value);
    if (!Number.isInteger(value) || value < 1 || value > 12) return;
    this.selectedMonth = value;
    this.clampMonthToCurrentIfNeeded();
    void this.loadSummary();
  };

  private clampMonthToCurrentIfNeeded() {
    const now = new Date();
    if (this.selectedYear === now.getUTCFullYear()) {
      this.selectedMonth = Math.min(this.selectedMonth, now.getUTCMonth() + 1);
    }
  }

  private renderMonthOptions() {
    const now = new Date();
    const limit =
      this.selectedYear === now.getUTCFullYear() ? now.getUTCMonth() + 1 : 12;
    return Array.from({ length: limit }, (_, i) => i + 1).map(
      (month) =>
        html`<sp-menu-item value=${String(month)}>${month}</sp-menu-item>`
    );
  }

  private renderYearOptions() {
    const now = new Date();
    const currentYear = now.getUTCFullYear();
    const minYear = 2020;
    return Array.from(
      { length: currentYear - minYear + 1 },
      (_, i) => currentYear - i
    ).map(
      (year) => html`<sp-menu-item value=${String(year)}>${year}</sp-menu-item>`
    );
  }

  render() {
    const summary = this.summary;
    const locale = getLocale();

    return html`
      <h1>${msg(html`Usage & Costs`)}${summary ? ` - ${summary.organizationName}` : ''}</h1>

      <div class="selector-row">
        <div class="selector-control">
          <sp-field-label for="usage-year">${msg('Year')}</sp-field-label>
          <sp-picker
            id="usage-year"
            .value=${String(this.selectedYear)}
            @change=${this.onYearChange}
          >
            ${this.renderYearOptions()}
          </sp-picker>
        </div>

        <div class="selector-control">
          <sp-field-label for="usage-month">${msg('Month')}</sp-field-label>
          <sp-picker
            id="usage-month"
            .value=${String(this.selectedMonth)}
            @change=${this.onMonthChange}
          >
            ${this.renderMonthOptions()}
          </sp-picker>
        </div>
      </div>

      ${this.loading ? html`<p class="muted">${msg('Loading usage...')}</p>` : null}
      ${this.errorMessage ? html`<p class="error">${this.errorMessage}</p>` : null}

      ${
        summary
          ? html`
            <div class="section">
              <h3>${msg('Organization totals')}</h3>
              <sp-table>
                <sp-table-head>
                  <sp-table-head-cell>${msg('Users in organization')}</sp-table-head-cell>
                  <sp-table-head-cell>${msg('Automations in organization')}</sp-table-head-cell>
                </sp-table-head>
                <sp-table-body>
                  <sp-table-row class="row">
                    <sp-table-cell>${summary.userCount}</sp-table-cell>
                    <sp-table-cell>${summary.workflowCount}</sp-table-cell>
                  </sp-table-row>
                </sp-table-body>
              </sp-table>
            </div>

            <div class="section">
              <h3>${msg('Chat usage')}</h3>
              <sp-table>
                <sp-table-head>
                  <sp-table-head-cell>${msg('Model')}</sp-table-head-cell>
                  <sp-table-head-cell>${msg('Input tokens')}</sp-table-head-cell>
                  <sp-table-head-cell>${msg('Output tokens')}</sp-table-head-cell>
                  <sp-table-head-cell>${msg('Costs')}</sp-table-head-cell>
                </sp-table-head>
                <sp-table-body>
                  <sp-table-row class="row">
                    <sp-table-cell><strong>${msg('Total')}</strong></sp-table-cell>
                    <sp-table-cell>${summary.chat.totals.inputTokens}</sp-table-cell>
                    <sp-table-cell>${summary.chat.totals.outputTokens}</sp-table-cell>
                    <sp-table-cell
                      ><strong>${formatCurrencyCents(summary.chat.totals.costs, 'EUR', locale)}</strong></sp-table-cell
                    >
                  </sp-table-row>
                  ${summary.chat.byModel.map(
                    (row) => html`
                      <sp-table-row class="row">
                        <sp-table-cell>${row.model}</sp-table-cell>
                        <sp-table-cell>${row.inputTokens}</sp-table-cell>
                        <sp-table-cell>${row.outputTokens}</sp-table-cell>
                        <sp-table-cell>${formatCurrencyCents(row.costs, 'EUR', locale)}</sp-table-cell>
                      </sp-table-row>
                    `
                  )}
                </sp-table-body>
              </sp-table>
            </div>

            <div class="section">
              <h3>${msg(html`Workflow executions & costs`)}</h3>
              <sp-table>
                <sp-table-head>
                  <sp-table-head-cell>${msg('Workflow')}</sp-table-head-cell>
                  <sp-table-head-cell>${msg('Executions')}</sp-table-head-cell>
                  <sp-table-head-cell>${msg('Execution time')}</sp-table-head-cell>
                  <sp-table-head-cell>${msg('Costs')}</sp-table-head-cell>
                </sp-table-head>
                <sp-table-body>
                  ${summary.workflows.executions.map(
                    (row) => html`
                      <sp-table-row class="row">
                        <sp-table-cell>${row.workflowName}</sp-table-cell>
                        <sp-table-cell>${row.executionCount}</sp-table-cell>
                        <sp-table-cell>${formatDuration(row.executionTime)}</sp-table-cell>
                        <sp-table-cell>${formatCurrencyCents(row.costs, 'EUR', locale)}</sp-table-cell>
                      </sp-table-row>
                    `
                  )}
                  <sp-table-row class="row">
                    <sp-table-cell><strong>${msg('Total')}</strong></sp-table-cell>
                    <sp-table-cell><strong>${summary.workflows.totals.executionCount}</strong></sp-table-cell>
                    <sp-table-cell><strong>${formatDuration(summary.workflows.totals.executionTime)}</strong></sp-table-cell>
                    <sp-table-cell
                      ><strong>${formatCurrencyCents(summary.workflows.totals.costs, 'EUR', locale)}</strong></sp-table-cell
                    >
                  </sp-table-row>
                </sp-table-body>
              </sp-table>
            </div>

            <div class="section">
              <h3>${msg(html`Task executions & costs`)}</h3>
              <sp-table>
                <sp-table-head>
                  <sp-table-head-cell>${msg('Task type')}</sp-table-head-cell>
                  <sp-table-head-cell>${msg('Executions')}</sp-table-head-cell>
                  <sp-table-head-cell>${msg('Costs')}</sp-table-head-cell>
                </sp-table-head>
                <sp-table-body>
                  ${summary.tasks.executions.map(
                    (row) => html`
                      <sp-table-row class="row">
                        <sp-table-cell>${row.taskType}</sp-table-cell>
                        <sp-table-cell>${row.executionCount}</sp-table-cell>
                        <sp-table-cell>${formatCurrencyCents(row.costs, 'EUR', locale)}</sp-table-cell>
                      </sp-table-row>
                    `
                  )}
                  <sp-table-row class="row">
                    <sp-table-cell><strong>${msg('Total')}</strong></sp-table-cell>
                    <sp-table-cell><strong>${summary.tasks.totals.executionCount}</strong></sp-table-cell>
                    <sp-table-cell
                      ><strong>${formatCurrencyCents(summary.tasks.totals.costs, 'EUR', locale)}</strong></sp-table-cell
                    >
                  </sp-table-row>
                </sp-table-body>
              </sp-table>
            </div>
          `
          : null
      }
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kv-module-usage': KvModuleUsage;
  }
}
